import React from 'react';
import { Row, Col, Form, Input, Select, DatePicker, InputNumber, Typography } from 'antd';
import { Sample } from '../../../../../types/lab/SampleTypes';
import { Company } from '../../../../../types/admin/CompanyTypes';
import {
	statusOptions,
	priorityLevelOptions,
} from '../../../../../types/lab/SampleTypes';
import { SectionCard } from '../../../../../components/shared';
import dayjs from 'dayjs';
import { sampleTypeOptions } from '../../../../../types/lab/SampleTypes';

const { Text } = Typography;
const { TextArea } = Input;

interface SampleDetailsTabProps {
	sampleData: Sample;
	updateSampleData: (field: string, value: any) => void;
	updateMultipleFields: (updates: Record<string, any>) => void;
	setErrorMessages: (messages: string[]) => void;
	getFieldError: (fieldName: string) => string | undefined;
	companies: Company[];
	disabled: boolean;
}

const SampleDetailsTab: React.FC<SampleDetailsTabProps> = ({
	sampleData,
	updateSampleData,
	updateMultipleFields,
	getFieldError,
	companies,
	disabled,
}) => {
	const handleDateChange = (field: string, date: any) => {
		updateSampleData(field, date ? date.toISOString() : null);
	};

	const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500 };

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Basic Information */}
			<SectionCard title="Basic Information">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Client Sample ID</Text>}>
							<Input
								value={sampleData.sampleOA?.client_sampleID}
								onChange={(e) => updateSampleData('sampleOA.client_sampleID', e.target.value)}
								disabled={!!sampleData.id}
								size="small"
								placeholder="Client sample identifier"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item 
							label={<Text style={labelStyle}>Client</Text>}
							help={getFieldError('client')}
							validateStatus={getFieldError('client') ? 'error' : ''}
						>
							<Select
								value={sampleData.client}
								onChange={(value) => updateSampleData('client', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Select client"
								showSearch
								filterOption={(input, option) =>
									(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
								}
								options={companies.map(company => ({
									label: company.name,
									value: company.name
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Internal ID</Text>}>
							<Input
								value={sampleData.id ? sampleData.id.toString() : 'Auto-generated'}
								disabled
								size="small"
								
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Processing Status</Text>}>
							<Select
								value={sampleData.sampleOA?.status}
								onChange={(value) => updateSampleData('sampleOA.status', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Select status"
								options={statusOptions}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Priority Level</Text>}>
							<Select
								value={sampleData.priority_level}
								onChange={(value) => updateSampleData('priority_level', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Select priority"
								options={priorityLevelOptions}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Sample Details */}
			<SectionCard title="Sample Details">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Sample Type</Text>}>
							<Select
								value={sampleData.sampleOA?.sampleType}
								onChange={(value) => updateSampleData('sampleOA.sampleType', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Select sample type"
								options={sampleTypeOptions}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Volume (μL)</Text>}>
							<InputNumber
								value={sampleData.sampleOA?.volume}
								onChange={(value) => updateSampleData('sampleOA.volume', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Sample volume"
								min={0}
								step={0.1}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Concentration (ng/μL)</Text>}>
							<InputNumber
								value={sampleData.sampleOA?.concentration}
								onChange={(value) => updateSampleData('sampleOA.concentration', value)}
								disabled={disabled}
								size="small"
								
								placeholder="DNA concentration"
								min={0}
								step={0.1}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>OD 260/280</Text>}>
							<InputNumber
								value={sampleData.sampleOA?.OD}
								onChange={(value) => updateSampleData('sampleOA.OD', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Optical density"
								min={0}
								step={0.01}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>OD 260/230</Text>}>
							<InputNumber
								value={sampleData.sampleOA?.OD2}
								onChange={(value) => updateSampleData('sampleOA.OD2', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Secondary OD"
								min={0}
								step={0.01}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Dates and Timeline */}
			<SectionCard title="Timeline">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Created Date</Text>}>
							<DatePicker
								value={sampleData.created_at ? dayjs(sampleData.created_at) : null}
								onChange={(date) => handleDateChange('created_at', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>QC Date</Text>}>
							<DatePicker
								value={sampleData.sampleOA?.QC_date ? dayjs(sampleData.sampleOA.QC_date) : null}
								onChange={(date) => handleDateChange('sampleOA.QC_date', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Expected Completion</Text>}>
							<DatePicker
								value={sampleData.estimated_completion ? dayjs(sampleData.estimated_completion) : null}
								onChange={(date) => handleDateChange('estimated_completion', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>OA Date</Text>}>
							<DatePicker
								value={sampleData.sampleOA?.OA_date ? dayjs(sampleData.sampleOA.OA_date) : null}
								onChange={(date) => handleDateChange('sampleOA.OA_date', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Reported Date</Text>}>
							<DatePicker
								value={sampleData.sampleOA?.reported_date ? dayjs(sampleData.sampleOA.reported_date) : null}
								onChange={(date) => handleDateChange('sampleOA.reported_date', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Last Status Change</Text>}>
							<DatePicker
								value={sampleData.last_status_change ? dayjs(sampleData.last_status_change) : null}
								onChange={(date) => handleDateChange('last_status_change', date)}
								disabled={disabled}
								size="small"
								
								showTime
								format="YYYY-MM-DD HH:mm:ss"
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Lab Information */}
			<SectionCard title="Lab Information">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Job ID</Text>}>
							<Input
								value={sampleData.sampleOA?.jobID?.toString() || ''}
								onChange={(e) => updateSampleData('sampleOA.jobID', e.target.value ? parseInt(e.target.value) : null)}
								disabled={disabled}
								size="small"
								
								placeholder="Lab job ID"
							/>
						</Form.Item>
					</Col>

					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Lab Location</Text>}>
							<Input
								value={sampleData.sampleOA?.lab_location || ''}
								onChange={(e) => updateSampleData('sampleOA.lab_location', e.target.value)}
								disabled={disabled}
								size="small"
								
								placeholder="Lab location"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Storage</Text>}>
							<Input
								value={sampleData.sampleOA?.storage || ''}
								onChange={(e) => updateSampleData('sampleOA.storage', e.target.value)}
								disabled={disabled}
								size="small"
								
								placeholder="Storage location"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Available Material</Text>}>
							<Input
								value={sampleData.sampleOA?.available_material || ''}
								onChange={(e) => updateSampleData('sampleOA.available_material', e.target.value)}
								disabled={disabled}
								size="small"
								
								placeholder="Available material types"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Call Rate %</Text>}>
							<InputNumber
								value={sampleData.sampleOA?.OA_call_rate}
								onChange={(value) => updateSampleData('sampleOA.OA_call_rate', value)}
								disabled={disabled}
								size="small"
								
								placeholder="Call rate percentage"
								min={0}
								max={100}
								step={0.1}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Comments and Notes */}
			<SectionCard title="Comments & Notes">
				<Row gutter={[16, 16]}>
					<Col span={12}>
						<Form.Item label={<Text style={labelStyle}>Comments</Text>}>
							<TextArea
								value={sampleData.sampleOA?.comments || ''}
								onChange={(e) => updateSampleData('sampleOA.comments', e.target.value)}
								disabled={disabled}
								size="small"
								
								placeholder="Sample comments"
								rows={4}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label={<Text style={labelStyle}>QC Notice</Text>}>
							<TextArea
								value={sampleData.sampleOA?.QC_notice || ''}
								onChange={(e) => updateSampleData('sampleOA.QC_notice', e.target.value)}
								disabled={disabled}
								size="small"
								
								placeholder="Quality control notices"
								rows={4}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>
		</div>
	);
};

export default SampleDetailsTab;